import React from "react";
import axiosInstance from "../../api/axiosInstance";
import commentary from "../../assets/images/commentary.png";
import MatchOddsSection from "../common/MatchOddsSection";
import FancyDetailRow from "../common/FancyDetailRow";
import PlaceBetOpenBets from "../common/PlaceBetOpenBets";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchMultiMarketDetails,
  fetchMultiMarketFancy,
} from "../../store/slices/multiMarketSlice";

const MultiMarket = () => {
  const dispatch = useDispatch();
  const { news } = useSelector((state) => state.news);
  const {
    markets,
    fancies,
    loading,
    error: loadError,
  } = useSelector((state) => state.multiMarket);

  const newsHeadingsText = React.useMemo(() => {
    const payload = news;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    return items
      .map((x) => x?.heading)
      .filter(Boolean)
      .join("  •  ");
  }, [news]);

  const [teamPositionByMatchId, setTeamPositionByMatchId] = React.useState({});
  const [teamPositionLoading, setTeamPositionLoading] = React.useState(false);
  const [teamPositionError, setTeamPositionError] = React.useState(null);
  const [fancyLiabilityByMatchId, setFancyLiabilityByMatchId] = React.useState(
    {},
  );

  React.useEffect(() => {
    dispatch(fetchMultiMarketDetails());
    dispatch(fetchMultiMarketFancy());

    const interval = setInterval(() => {
      dispatch(fetchMultiMarketDetails());
    }, 5000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const dedupedMarketBookmarks = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const market of Array.isArray(markets) ? markets : []) {
      const matchId = market?.match_id ?? market?.matchId;
      const marketId = market?.market_id ?? market?.marketId ?? market?.id;
      if (matchId == null || marketId == null) continue;
      const key = `${String(matchId)}::${String(marketId)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        matchId: String(matchId),
        marketId: String(marketId),
        marketTitle: market?.market_name ?? market?.name ?? "",
        _market: market,
      });
    }
    return out;
  }, [markets]);

  const fancyBookmarks = React.useMemo(() => {
    return (Array.isArray(fancies) ? fancies : [])
      .map((item) => {
        const matchId = item?.match_id ?? item?.matchId;
        const fancyId = item?.fancy_id ?? item?.selection_id ?? item?.fancyId;
        if (matchId == null || fancyId == null) return null;
        return {
          matchId: String(matchId),
          fancyId: String(fancyId),
          label: item?.name || item?.fancy_name || "",
        };
      })
      .filter(Boolean);
  }, [fancies]);

  const marketBookmarks = React.useMemo(() => {
    return dedupedMarketBookmarks.map((item) => ({
      matchId: item.matchId,
      marketId: item.marketId,
      marketTitle: item.marketTitle,
    }));
  }, [dedupedMarketBookmarks]);

  const uniqueMatchIds = React.useMemo(() => {
    const ids = [
      ...marketBookmarks.map((b) => b?.matchId),
      ...fancyBookmarks.map((b) => b?.matchId),
    ].filter((x) => x != null && String(x).trim() !== "");
    return Array.from(new Set(ids.map(String)));
  }, [marketBookmarks, fancyBookmarks]);

  const uniqueFancyMatchIds = React.useMemo(() => {
    const ids = fancyBookmarks
      .map((b) => b?.matchId)
      .filter((x) => x != null && String(x).trim() !== "");
    return Array.from(new Set(ids.map(String)));
  }, [fancyBookmarks]);

  const fetchTeamPositionForMatch = React.useCallback(async (matchId) => {
    const payload = {
      match_id: matchId,
    };
    const response = await axiosInstance.post("/bet/getTeamPosition", payload);
    return response?.data ?? response;
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!uniqueMatchIds.length) {
        setTeamPositionByMatchId({});
        return;
      }

      setTeamPositionLoading(true);
      setTeamPositionError(null);
      try {
        const results = {};
        // Fetch sequentially to avoid API burst
        // eslint-disable-next-line no-restricted-syntax
        for (const matchId of uniqueMatchIds) {
          // eslint-disable-next-line no-await-in-loop
          results[matchId] = await fetchTeamPositionForMatch(matchId);
        }
        if (!cancelled) setTeamPositionByMatchId(results);
      } catch (e) {
        if (!cancelled) setTeamPositionError(e);
      } finally {
        if (!cancelled) setTeamPositionLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [uniqueMatchIds, fetchTeamPositionForMatch]);

  const fetchFancyLiabilityForMatch = React.useCallback(async (matchId) => {
    const payload = {
      match_id: matchId,
    };
    const response = await axiosInstance.post(
      "/bet/getFancyLiability",
      payload,
    );
    return response?.data ?? response;
  }, []);

  React.useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!uniqueFancyMatchIds.length) {
        setFancyLiabilityByMatchId({});
        return;
      }

      try {
        const liabilityResults = {};

        // eslint-disable-next-line no-restricted-syntax
        for (const matchId of uniqueFancyMatchIds) {
          // eslint-disable-next-line no-await-in-loop
          liabilityResults[matchId] =
            await fetchFancyLiabilityForMatch(matchId);
        }

        if (!cancelled) {
          setFancyLiabilityByMatchId(liabilityResults);
        }
      } catch (e) {
        if (!cancelled) setFancyLiabilityByMatchId({});
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [uniqueFancyMatchIds, fetchFancyLiabilityForMatch]);

  const favoriteFanciesByMatchId = React.useMemo(() => {
    const grouped = {};
    for (const item of Array.isArray(fancies) ? fancies : []) {
      const matchId = item?.match_id ?? item?.matchId;
      if (matchId == null) continue;
      const key = String(matchId);
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(item);
    }
    return grouped;
  }, [fancies]);

  const fancyIdsByMatchId = React.useMemo(() => {
    const out = {};
    for (const b of fancyBookmarks) {
      if (b?.matchId == null || b?.fancyId == null) continue;
      const key = String(b.matchId);
      if (!out[key]) out[key] = new Set();
      out[key].add(String(b.fancyId));
    }
    return out;
  }, [fancyBookmarks]);

  const normalizeFancyItemsForMatch = React.useCallback(
    (matchId) => {
      const rawFancies = favoriteFanciesByMatchId[matchId];

      // Normalize fancy shapes to an array
      let fancyArray = [];
      if (Array.isArray(rawFancies)) {
        fancyArray = rawFancies;
      } else if (rawFancies && typeof rawFancies === "object") {
        if (Array.isArray(rawFancies.data)) fancyArray = rawFancies.data;
        else if (Array.isArray(rawFancies?.[0]?.data))
          fancyArray = rawFancies[0].data;
      }

      const rawLiability = fancyLiabilityByMatchId[matchId];
      let liabilityMap = {};
      if (rawLiability && typeof rawLiability === "object") {
        if (rawLiability.data && typeof rawLiability.data === "object") {
          liabilityMap = rawLiability.data;
        } else if (Array.isArray(rawLiability) && rawLiability[0]) {
          liabilityMap = rawLiability[0];
        } else {
          liabilityMap = rawLiability;
        }
      }

      const isInplay = Boolean(
        dedupedMarketBookmarks.find((bm) => bm.matchId === String(matchId))
          ?._market?.inplay,
      );
      const wanted = fancyIdsByMatchId[matchId] || new Set();

      const normalized = fancyArray.map((item, index) => {
        const id = String(
          item?.fancy_id || item?.selection_id || `fancy-${index}`,
        );

        const rawGameStatus =
          typeof item?.GameStatus === "string" ? item.GameStatus : "";
        const trimmedGameStatus = rawGameStatus.trim();
        const isSuspended =
          trimmedGameStatus !== "" &&
          trimmedGameStatus.toUpperCase() === "SUSPENDED";

        const lay = item?.LayPrice1 ?? "";
        const laySize = item?.LaySize1 ?? "";
        const back = item?.BackPrice1 ?? "";
        const backSize = item?.BackSize1 ?? "";

        const minStake = isInplay
          ? item?.session_min_stack
          : item?.session_before_inplay_min_stack;
        const maxStake = isInplay
          ? item?.session_max_stack
          : item?.session_before_inplay_max_stack;

        const rawLiabilityValue =
          liabilityMap[id] ??
          liabilityMap[String(item?.fancy_id)] ??
          liabilityMap[String(item?.selection_id)];

        return {
          id,
          label: item?.name || item?.fancy_name || "",
          labelSuffix: "",
          fancyId: item?.fancy_id ?? null,
          selectionId: item?.selection_id ?? null,
          marketId: item?.market_id ?? item?.marketId ?? null,
          lay,
          laySize,
          back,
          backSize,
          minStake,
          maxStake,
          // FancyDetailRow will treat backOnly as lay === ""
          backOnly: lay === "",
          gameStatus: trimmedGameStatus,
          isSuspended,
          liability:
            typeof rawLiabilityValue === "number"
              ? rawLiabilityValue
              : rawLiabilityValue != null
                ? Number(rawLiabilityValue) || 0
                : 0,
        };
      });

      return normalized.filter((it) => wanted.has(String(it.id)));
    },
    [
      favoriteFanciesByMatchId,
      fancyLiabilityByMatchId,
      dedupedMarketBookmarks,
      fancyIdsByMatchId,
    ],
  );

  const buildRunnersFromMarket = React.useCallback(
    (market, marketPositions) => {
      if (!market || !Array.isArray(market.runners)) return [];

      const marketKey =
        market?.market_id ?? market?.id ?? market?.marketId ?? null;
      const marketStatus = String(market?.status || "").toUpperCase();
      const positionsForMarket = Array.isArray(marketPositions)
        ? marketPositions
        : [];

      const to3 = (arr) => {
        const prices = ["-", "-", "-"];
        const sizes = ["0.0", "0.0", "0.0"];

        for (let i = 0; i < 3; i += 1) {
          const q = arr?.[i];
          if (q && typeof q === "object") {
            const price = q.price ?? q.odds ?? q.rate ?? q[0];
            const size = q.size ?? q.amount ?? q.volume ?? q[1];
            prices[i] = price != null ? String(price) : "-";
            sizes[i] = size != null ? String(size) : "0.0";
          } else if (q != null) {
            prices[i] = String(q);
            sizes[i] = "0.0";
          }
        }

        return { prices, sizes };
      };

      return market.runners.map((runner) => {
        const runnerStatus = String(runner?.status || "").toUpperCase();
        const isSuspended =
          marketStatus === "SUSPENDED" || runnerStatus === "SUSPENDED";

        const selectionId =
          runner?.selectionId ??
          runner?.selection_id ??
          runner?.id ??
          runner?._id ??
          null;

        const position =
          positionsForMarket.find(
            (p) =>
              String(p?.selection_id) === String(selectionId) ||
              String(p?.selectionId) === String(selectionId),
          ) || null;
        const userPlRaw =
          position?.user_pl ?? position?.win_loss ?? position?.winLoss ?? null;

        const backs = runner?.ex?.availableToBack || [];
        const lays = runner?.ex?.availableToLay || [];

        const { prices: backPrices, sizes: backSizes } = to3(backs);
        const { prices: layPrices, sizes: laySizes } = to3(lays);

        return {
          name: runner?.selection_name ?? runner?.name ?? "",
          selectionId,
          marketId: marketKey,
          isSuspended,
          backPrices,
          backSizes,
          layPrices,
          laySizes,
          userPl:
            position == null
              ? null
              : typeof userPlRaw === "number"
                ? userPlRaw
                : userPlRaw != null
                  ? Number(userPlRaw) || 0
                  : 0,
        };
      });
    },
    [],
  );

  const getMinMax = React.useCallback((market) => {
    if (!market) return { minStake: null, maxStake: null, minMaxText: "" };

    const minStakeRaw = market?.inplay
      ? market?.market_min_stack
      : (market?.market_advance_bet_min_stake ??
        market?.market_min_stack ??
        null);
    const maxStakeRaw = market?.inplay
      ? market?.market_max_stack
      : (market?.market_advance_bet_stake ?? market?.market_max_stack ?? null);

    const minStakeNum = Number.isFinite(Number(minStakeRaw))
      ? Number(minStakeRaw)
      : null;
    const maxStakeNum = Number.isFinite(Number(maxStakeRaw))
      ? Number(maxStakeRaw)
      : null;

    return {
      minStake: minStakeNum,
      maxStake: maxStakeNum,
      minMaxText: `Min: ${minStakeNum != null ? minStakeNum : "-"} | Max: ${
        maxStakeNum != null ? maxStakeNum : "-"
      }`,
    };
  }, []);

  const handleOddsClick = (payload) => {
    dispatch({ type: "game/setSelectedBet", payload });
  };

  return (
    <section className="section listing_page">
      <div className="row">
        <div className="col-md-12 col-lg-8 detail-center-column">
          <div className="col-12 d-flex">
            <div className="commentary">
              <img src={commentary} alt="commentary" />
              <div className="commentary-ticker" aria-live="polite">
                <span>{newsHeadingsText || "Welcome..."}</span>
              </div>
            </div>
          </div>
          <div className="middle-page-content details-page">
            <div className="card-header game-heading">
              <span className="card-header-title">Multi Market</span>
            </div>
            <div>
              {dedupedMarketBookmarks.length === 0 &&
              fancyBookmarks.length === 0 ? (
                <div className="py-3 text-center"></div>
              ) : (
                <>
                  {loading &&
                    dedupedMarketBookmarks.length === 0 &&
                    fancyBookmarks.length === 0 && (
                      <div className="py-3 text-center">Loading...</div>
                    )}
                  {loadError && (
                    <div className="py-3 text-center text-red-500">
                      Failed to load bookmarked markets
                    </div>
                  )}
                  {teamPositionError && (
                    <div className="py-2 text-center text-red-500">
                      Failed to load team positions (cashout may be unavailable)
                    </div>
                  )}

                  {!loadError &&
                    dedupedMarketBookmarks.map((bm, index) => {
                      const matchIdStr =
                        bm?.matchId != null ? String(bm.matchId) : "";
                      const marketIdStr =
                        bm?.marketId != null ? String(bm.marketId) : "";

                      if (!matchIdStr || !marketIdStr) return null;
                      const market = bm?._market;

                      if (!market) return null;

                      const title =
                        market?.market_name ??
                        market?.name ??
                        bm?.marketTitle ??
                        "Match Odds";

                      const { minStake, maxStake, minMaxText } =
                        getMinMax(market);
                      const minStakeForBet = minStake != null ? minStake : 100;
                      const maxStakeForBet =
                        maxStake != null ? maxStake : 25000;

                      const teamPositionForMatch =
                        teamPositionByMatchId?.[matchIdStr] &&
                        typeof teamPositionByMatchId[matchIdStr] === "object"
                          ? teamPositionByMatchId[matchIdStr]
                          : null;
                      const marketPositions =
                        teamPositionForMatch?.[marketIdStr] ??
                        teamPositionForMatch?.[String(marketIdStr)] ??
                        [];
                      const showCashout =
                        Array.isArray(marketPositions) &&
                        marketPositions.length > 0 &&
                        Array.isArray(market?.runners) &&
                        market.runners.length === 2;

                      return (
                        <MatchOddsSection
                          key={`${matchIdStr}-${marketIdStr}-${index}`}
                          title={title}
                          minMaxText={minMaxText}
                          minStake={minStakeForBet}
                          maxStake={maxStakeForBet}
                          collapseId={marketIdStr}
                          runners={buildRunnersFromMarket(
                            market,
                            marketPositions,
                          )}
                          onOddsClick={handleOddsClick}
                          matchId={matchIdStr}
                          market={market}
                          marketId={marketIdStr}
                          initialBookmarked={true}
                          marketPositions={marketPositions}
                          showCashout={!teamPositionLoading && showCashout}
                          showMarquee={title
                            .toUpperCase()
                            .includes("BOOKMAKER")}
                          marqueeText={
                            "England V India : Advance BM ,Toss & Fancy Bets Started🏏"
                          }
                        />
                      );
                    })}

                  {dedupedMarketBookmarks.length === 0 &&
                    fancyBookmarks.length === 0 &&
                    !loading &&
                    !loadError && (
                      <div className="py-3 text-center">
                        No bookmarked markets found
                      </div>
                    )}

                  {fancyBookmarks.length > 0 && (
                    <div className="newtab_collect tab-container mt-3">
                      <div className="fancy-heading-bar">
                        <span>Fancy</span>
                      </div>

                      {!loadError && (
                        <div className="tab-content">
                          <div className="tab-pane active">
                            <div className="market_section twoOddsSection">
                              <div className="fancy-tab">
                                <div className="tab-container">
                                  <div className="fancy-tab-content">
                                    {/*
                                      Keep the same layout wrappers as SportDetail
                                      so FancyDetailRow columns render correctly.
                                    */}
                                    <div className="row align-items-center oddsHeader">
                                      <div className="col-lg-6 col-md-6 col-7 px-0" />
                                      <div className="col-lg-2 col-md-2 col-1 px-0" />
                                      <div className="col-lg-2 col-md-2 col-4 px-0">
                                        <div className="btn-group">
                                          <button
                                            type="button"
                                            className="w-100 lay"
                                          >
                                            no
                                          </button>
                                          <button
                                            type="button"
                                            className="w-100 back"
                                          >
                                            yes
                                          </button>
                                        </div>
                                      </div>
                                      <div className="col-lg-2 col-md-2 col-4 px-0 d-none d-md-block" />
                                    </div>

                                    {uniqueFancyMatchIds.map((matchIdStr) => {
                                      const items =
                                        normalizeFancyItemsForMatch(matchIdStr);
                                      if (!items.length) return null;

                                      return items.map((item) => (
                                        <FancyDetailRow
                                          key={`${matchIdStr}-${item.id}`}
                                          item={item}
                                          onOddsClick={handleOddsClick}
                                          matchId={matchIdStr}
                                          marketId={item.marketId}
                                          initialBookmarked={true}
                                        />
                                      ));
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-12 col-lg-4 detail-right-column d-none d-lg-block px-0">
          <PlaceBetOpenBets
            autoFetchOpenBets
            filterToBookmarks
            bookmarkedMarkets={marketBookmarks}
            bookmarkedFancies={fancyBookmarks}
          />
        </div>
      </div>
    </section>
  );
};

export default MultiMarket;
